import { Link } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/shared/components/EmptyState";
import { SectionCard } from "@/shared/components/SectionCard";
import { Tooltip } from "@/shared/ui/tooltip";

import type { PromotionPerformance } from "../api/use-promotion-performance";
import { DASH, formatMoney, formatShare, sortBrandRows } from "../lib/performance";

// DataTable rhythm (docs/design/templates.md): 52px rows, px-4 cells, light dividers.
const TH =
  "h-10 whitespace-nowrap px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";
const TH_NUM =
  "h-10 whitespace-nowrap px-4 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";
const TD = "h-[52px] px-4 text-sm text-[var(--text-primary)]";
const TD_NUM =
  "h-[52px] whitespace-nowrap px-4 text-right text-sm tabular-nums text-[var(--text-primary)]";
const ROW = "border-t border-[var(--row-divider)] hover:bg-[var(--row-hover)]";
const TOTAL = "border-t border-[var(--premium-border-strong)] font-semibold";
// Name cells absorb the table's spare width and truncate to one line (full name in `title`).
// `max-w-0` lets the cell shrink below its content; the 2.5×--sp-20 (200px) floor makes narrow
// viewports scroll the table inside its card instead of stacking long Thai names 8 lines deep.
const NAME_TD = `${TD} w-full max-w-0 min-w-[calc(var(--sp-20)*2.5)]`;
const NAME_TEXT = "block truncate";
// Share column bounded to 2×--sp-20 (160px) so the Brand column absorbs spare width, not Share.
const SHARE_W = "w-[calc(var(--sp-20)*2)]";
const SHARE_PCT = "text-xs tabular-nums text-[var(--text-secondary)]";
const TOP_DEFAULT = 10;

interface TableProps {
  data: PromotionPerformance;
}

function Empty(): JSX.Element {
  const { t } = useTranslation("promotion");
  return <EmptyState title={t("performance.empty")} body={t("performance.empty_body")} />;
}

export function BrandTable({ data }: TableProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const rows = sortBrandRows(data.by_brand ?? []);
  return (
    <SectionCard title={t("performance.brand.title")} titleTestId="perf-brand-title">
      {rows.length === 0 ? (
        <Empty />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="perf-brand-table">
            <thead>
              <tr>
                <th className={TH}>{t("performance.col.brand")}</th>
                <th className={TH_NUM}>{t("performance.col.orders")}</th>
                <th className={TH_NUM}>{t("performance.col.units")}</th>
                <th className={TH_NUM}>{t("performance.col.gmv")}</th>
                <th className={`${TH} ${SHARE_W}`}>{t("performance.col.share")}</th>
                <th className={TH_NUM}>{t("performance.col.abv")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const unmapped = r.ap_name === null || r.ap_name === undefined;
                const skuCount = r.unmapped_sku_count ?? 0;
                return (
                  <tr
                    key={r.ap_name ?? "unmapped"}
                    className={ROW}
                    data-testid="perf-brand-row"
                    data-unmapped={String(unmapped)}
                  >
                    <td className={TD}>
                      {unmapped ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Link
                            to="/inventory"
                            search={{ search: "", offset: 0, brand: "unmapped" }}
                            data-testid="perf-unmapped-link"
                            className="font-semibold text-[var(--brand-600)] hover:underline"
                          >
                            {t("performance.brand.unmapped")}
                          </Link>
                          <Tooltip content={t("performance.brand.unmapped_tip", { n: skuCount })}>
                            <button
                              type="button"
                              aria-label={t("performance.brand.unmapped_tip", { n: skuCount })}
                              className="text-[var(--text-subtle)]"
                            >
                              <Info className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          </Tooltip>
                          <span className="text-xs text-[var(--text-subtle)]">
                            {t("performance.brand.unmapped_skus", { n: skuCount })}
                          </span>
                        </span>
                      ) : (
                        r.ap_name
                      )}
                    </td>
                    <td className={TD_NUM}>{r.orders.toLocaleString("en-US")}</td>
                    <td className={TD_NUM}>{r.units.toLocaleString("en-US")}</td>
                    <td className={TD_NUM}>{formatMoney(r.gmv)}</td>
                    <td className={TD}>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
                          {/* Spec §5.2: share bar is an inline div width, no chart lib. */}
                          <div
                            className="h-full rounded-full bg-[var(--brand-200)]"
                            style={{ width: `${Math.min(100, r.share_pct)}%` }}
                            data-testid="perf-share-bar"
                          />
                        </div>
                        <span className={SHARE_PCT}>{formatShare(r.share_pct)}</span>
                      </div>
                    </td>
                    <td className={TD_NUM}>{formatMoney(r.abv)}</td>
                  </tr>
                );
              })}
              <tr className={TOTAL} data-testid="perf-brand-total">
                <td className={TD}>{t("performance.total")}</td>
                <td className={TD_NUM}>{data.orders.toLocaleString("en-US")}</td>
                <td className={TD_NUM}>{data.units.toLocaleString("en-US")}</td>
                <td className={TD_NUM}>{formatMoney(data.gmv)}</td>
                <td className={TD}>
                  <div className="flex items-center gap-2">
                    {/* Spacer the width of the share track, so 100% lines up with the row figures. */}
                    <div className="invisible h-1.5 w-24" aria-hidden />
                    <span className={SHARE_PCT}>{formatShare(100)}</span>
                  </div>
                </td>
                <td className={TD_NUM}>{formatMoney(data.abv)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export function GiftsTable({ data }: TableProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const items = data.gifts.items ?? [];
  return (
    <SectionCard title={t("performance.gifts.title")}>
      {items.length === 0 ? (
        <Empty />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="perf-gifts-table">
            <thead>
              <tr>
                <th className={TH}>{t("performance.col.gift_sku")}</th>
                <th className={TH}>{t("performance.col.name")}</th>
                <th className={TH_NUM}>{t("performance.col.qty")}</th>
                <th className={TH_NUM}>{t("performance.col.orders")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.gwp_sku} className={ROW}>
                  <td className={`${TD} sku`}>{g.gwp_sku}</td>
                  <td className={NAME_TD}>
                    <span className={NAME_TEXT} title={g.product_name || undefined}>
                      {g.product_name || DASH}
                    </span>
                  </td>
                  <td className={TD_NUM}>{g.qty.toLocaleString("en-US")}</td>
                  <td className={TD_NUM}>{g.orders.toLocaleString("en-US")}</td>
                </tr>
              ))}
              <tr className={TOTAL}>
                <td className={TD} colSpan={2}>
                  {t("performance.total")}
                </td>
                <td className={TD_NUM}>{data.gifts.distributed.toLocaleString("en-US")}</td>
                <td className={TD_NUM}>{data.orders.toLocaleString("en-US")}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export function TopSkusTable({ data }: TableProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const [showAll, setShowAll] = useState(false);
  const all = data.by_sku ?? [];
  const rows = showAll ? all : all.slice(0, TOP_DEFAULT);
  return (
    <SectionCard
      title={t("performance.top.title")}
      {...(all.length > TOP_DEFAULT
        ? {
            actions: (
              <button
                type="button"
                data-testid="perf-top-toggle"
                onClick={() => setShowAll((v) => !v)}
                className="text-xs font-semibold text-[var(--brand-600)] hover:underline"
              >
                {showAll
                  ? t("performance.top.show_less")
                  : t("performance.top.show_all", { n: all.length })}
              </button>
            ),
          }
        : {})}
    >
      {rows.length === 0 ? (
        <Empty />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="perf-top-table">
            <thead>
              <tr>
                <th className={TH}>{t("performance.col.sku")}</th>
                <th className={TH}>{t("performance.col.name")}</th>
                <th className={TH}>{t("performance.col.brand")}</th>
                <th className={TH_NUM}>{t("performance.col.units")}</th>
                <th className={TH_NUM}>{t("performance.col.gmv")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.sino_sku} className={ROW} data-testid="perf-top-row">
                  <td className={`${TD} sku`}>{s.sino_sku}</td>
                  <td className={NAME_TD}>
                    <span className={NAME_TEXT} title={s.product_name || undefined}>
                      {s.product_name || DASH}
                    </span>
                  </td>
                  <td className={TD}>{s.ap_name ?? DASH}</td>
                  <td className={TD_NUM}>{s.units.toLocaleString("en-US")}</td>
                  <td className={TD_NUM}>{formatMoney(s.gmv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
